/**
 * CandidateShell -- Minimal Candidate Layout
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * Candidate routes render WITHOUT the admin sidebar or header.
 * Provides a minimal XYDEN header bar per frontend_screen_design.md Section 5c.
 * Page components provide their own <main> landmark.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LiveAnnouncer } from "../components/LiveAnnouncer";
import { FocusManager } from "../components/FocusManager";
import { Button } from "../components/ui/button";
import { useAuthStore } from "../stores/authStore";
import { useActiveTestStore, withActiveTestId } from "../stores/activeTestStore";
import { authService } from "../services/authService";
import { apiClient } from "../services/apiClient";
import { LogOut } from "lucide-react";
import { AssessmentInstructionsDialog } from "../components/AssessmentInstructionsDialog";

export function CandidateShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuthStore();
  const activeTestId = useActiveTestStore((s) => s.testId);
  const clearActiveTest = useActiveTestStore((s) => s.clear);
  const [testName, setTestName] = useState("");

  // The assessments list is the picker — the candidate isn't working inside any
  // test there, so the header must never name one. `activeTestId` alone isn't
  // enough: it persists in sessionStorage after a pick, so browser-Back to the
  // list would otherwise still show the previously selected test's name.
  const onAssessmentsList = location.pathname === "/candidate/assessments";

  useEffect(() => {
    // Show a name only on a taking page (welcome/tp/aq) once a test is selected.
    // On the list, or with no selection, clear it and skip the fetch — otherwise
    // the backend's "most actionable participant" fallback leaks a name.
    if (!activeTestId || onAssessmentsList) {
      setTestName("");
      return;
    }
    // Guard against a stale resolve: if the candidate navigates (e.g. back to
    // the list) before this fetch settles, the cleanup flips `cancelled` so the
    // late response can't overwrite the freshly-cleared name.
    let cancelled = false;
    apiClient
      .get<{ test?: { name?: string } }>(withActiveTestId("/api/candidate/assessment"))
      .then((data) => {
        if (cancelled) return;
        const raw = data.test?.name ?? "";
        // Make machine-style names human-readable for candidates
        setTestName(raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeTestId, onAssessmentsList]);

  const handleLogout = useCallback(() => {
    authService
      .logout()
      .catch(() => {})
      .finally(() => {
        clearActiveTest();
        logout();
        navigate("/login");
      });
  }, [clearActiveTest, logout, navigate]);

  return (
    <>
      <LiveAnnouncer />
      <div className="h-screen flex flex-col bg-white overflow-hidden">
        {/* Minimal top bar */}
        <header className="flex-shrink-0 z-50 shadow-sm bordeb border-brand-steel/30 bg-white px-4">
          <div className="max-w-5xl mx-auto h-14 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-brand-crimson flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <span className="text-base font-bold text-slate-900 tracking-tight">XYDEN</span>
            </div>
            <div className="flex items-center gap-3">
              {user?.name && (
                <span className="text-sm font-semibold text-gray-900">{user.name.split(" ")[0]}</span>
              )}
              {testName && <span className="text-xs text-gray-600">{testName}</span>}
              <AssessmentInstructionsDialog />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-600 hover:text-brand-error inline-flex items-center gap-1.5 transition-colors"
              >
                <LogOut size={14} aria-hidden="true" />
                <span className="text-xs">Logout</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Content area */}
        <div id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto">
          <FocusManager />
          <Outlet />
        </div>
      </div>
    </>
  );
}

export default CandidateShell;
